import React from 'react';
import { HeadingSmall } from './Heading';
import styled, { css } from 'styled-components';
import Disclosure, { DisclosureParams } from './Disclosure';
import { default as _Icon, IconType } from './Icon';

type AccordionType = 'default' | 'arrow';
export const ACCORDION_HOR_PAD = '1.3em'; // This leaves space for the arrow
export const ACCORDION_VER_PAD = '1em';

const Icon = styled(_Icon)<{ $expanded?: boolean; $type: AccordionType }>`
    ${({ $type }): ReturnType<typeof css> =>
        $type === 'arrow'
            ? css`
                  font-size: var(--fontSizeFixed2);
                  width: ${ACCORDION_HOR_PAD};
              `
            : css`
                  font-size: var(--fontSizeFixed4);
              `}
    transform: rotate(
        ${({ $expanded, $type }): string =>
        $expanded && $type === 'default' ? '45deg' : '0deg'}
    );
`;

export const Accordion = styled.div`
    display: grid;
    grid-template-columns: 100%;

    --rowBorder: 1px solid var(--borderColor);

    & > :last-child {
        border-bottom: var(--rowBorder);
    }
`;

const Heading = styled(HeadingSmall)`
    font-size: var(--fontSizeFixed2);
    scroll-margin-top: var(--navbarHeightCollapsed);
    -webkit-tap-highlight-color: transparent;
`;

export const Button = styled.div<{
    $compact?: boolean;
    $type: AccordionType;
}>`
    display: flex;
    ${({ $type }): ReturnType<typeof css> | null =>
        $type === 'default'
            ? css`
                  align-items: center;
                  justify-content: space-between;
              `
            : null}
    width: 100%;
    border-top: var(--rowBorder);
    text-align: left;
    color: var(--foregroundColor);
    line-height: var(--lineHeightHeading2);
    padding: ${({ $compact }): string =>
            $compact ? 'var(--spacing2)' : 'var(--spacing3)'}
        0;
    margin: 0;
`;

const Panel = styled.div<{ $compact?: boolean; $type: AccordionType }>`
    margin: var(--spacing1) 0;
    padding-bottom: ${({ $compact }): string =>
        $compact ? 'var(--spacing2)' : 'var(--spacing5)'};

    ${({ $type }): ReturnType<typeof css> | null =>
        $type === 'arrow'
            ? css`
                  margin: 0;
                  padding: 0;
                  color: var(--foregroundColor);
                  background-color: var(--menuBackgroundColor);
              `
            : null}
`;

export function AccordionItem({
    heading,
    headingLevel,
    panel,
    compact,
    hidePanelUnlessExpanded,
    type = 'default',
    preExpanded,
    onExpand,
    id,
}: {
    heading?: React.ReactNode;
    headingLevel: number;
    panel: React.ReactNode;
    compact?: boolean;
    hidePanelUnlessExpanded?: boolean;
    type?: AccordionType;
    preExpanded?: boolean;
    onExpand?: () => void;
    id?: string;
}): React.ReactElement {
    return (
        <Disclosure preExpanded={preExpanded} onExpand={onExpand}>
            {({
                expanded,
                buttonAttributes,
                panelAttributes,
            }: DisclosureParams): React.ReactElement => (
                <span>
                    <Heading role="heading" aria-level={headingLevel} id={id}>
                        <Button
                            {...buttonAttributes}
                            as={panel ? 'button' : 'span'}
                            $compact={compact}
                            $type={type}
                        >
                            {type === 'arrow' && (
                                <Icon
                                    type={
                                        expanded
                                            ? IconType.CHEVRON_DOWN
                                            : IconType.CHEVRON_RIGHT
                                    }
                                    $type={type}
                                />
                            )}
                            <span>{heading}</span>
                            {type === 'default' && (
                                <Icon
                                    type={IconType.PLUS}
                                    $type={type}
                                    $expanded={Boolean(expanded)}
                                />
                            )}
                        </Button>
                    </Heading>

                    <Panel {...panelAttributes} $compact={compact} $type={type}>
                        {!hidePanelUnlessExpanded || expanded ? panel : null}
                    </Panel>
                </span>
            )}
        </Disclosure>
    );
}
